/**
 * CreateServiceDialog Component (TASK-215 Phase 2)
 *
 * Two-step dialog for creating/editing services:
 *   Step 1 (create only): Type selection
 *     (calculator / pricelist / classifier / questionnaire — TASK-332)
 *   Step 2: Type-specific form with dynamic tables
 *
 * In edit mode (service prop provided), step 1 is skipped and form is pre-filled.
 *
 * TASK-332 «Опросник» (questionnaire): the form shows a question-rows editor
 * (name, type select Число/Текст/Выбор/Документ, unit for number, description,
 * required checkbox, options for enum) INSTEAD of formula/positions/tariffs.
 * No chain is created for a questionnaire. Server-side validation errors under
 * the `questionnaire_fields` key are surfaced next to the form.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Calculator, ClipboardList, List, Tag, X, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { servicesApi } from '@/services/api/services';
import type {
  Service,
  CreateServiceData,
  PricingItem,
  QuestionnaireField,
} from '@/services/api/services';
import { CalculatorForm } from '@/components/services/create-form/CalculatorForm';
import { PricelistForm } from '@/components/services/create-form/PricelistForm';
import { ClassifierForm } from '@/components/services/create-form/ClassifierForm';
import { QuestionnaireForm } from '@/components/services/create-form/QuestionnaireForm';
import type {
  ConstantRow,
  PricingRow,
  QuestionRow,
  RequiredInputRow,
} from '@/components/services/create-form/types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDescendantIds(serviceId: number, services: Service[]): Set<number> {
  const result = new Set<number>([serviceId]);
  const queue = [serviceId];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    services.filter(s => s.parent === cur).forEach(s => {
      if (!result.has(s.id)) { result.add(s.id); queue.push(s.id); }
    });
  }
  return result;
}

// Extract a DRF validation error for a given field key from an axios-shaped
// error. Returns the joined message(s) or null when the key is absent.
function extractFieldError(error: unknown, key: string): string | null {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  const value = data?.[key];
  if (value === undefined || value === null) return null;
  return Array.isArray(value) ? value.map(String).join(' ') : String(value);
}

// ── Types ────────────────────────────────────────────────────────────────────

type CalculationMethod = 'calculator' | 'pricelist' | 'classifier' | 'questionnaire';

interface CreateServiceDialogProps {
  open: boolean;
  onClose: () => void;
  parentId?: number | null;
  service?: Service;
}

// ── Localized labels ─────────────────────────────────────────────────────────

const METHOD_LABELS: Record<CalculationMethod, string> = {
  calculator: 'Калькулятор',
  pricelist: 'Прайс-лист',
  classifier: 'Классификатор',
  questionnaire: 'Опросник',
};

// ── Component ────────────────────────────────────────────────────────────────

export function CreateServiceDialog({
  open,
  onClose,
  parentId,
  service,
}: CreateServiceDialogProps): JSX.Element | null {
  const isEdit = !!service;
  const queryClient = useQueryClient();

  // ── State ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<'type' | 'form'>('type');
  const [selectedType, setSelectedType] = useState<CalculationMethod | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');

  // Calculator-specific
  const [requiredInputs, setRequiredInputs] = useState<RequiredInputRow[]>([]);
  const [constants, setConstants] = useState<ConstantRow[]>([]);
  const [formula, setFormula] = useState('');

  // Parent service
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);

  // Pricelist/Classifier-specific
  const [pricingRows, setPricingRows] = useState<PricingRow[]>([]);
  const [postFormulaEnabled, setPostFormulaEnabled] = useState(false);
  const [postFormula, setPostFormula] = useState('');

  // Questionnaire-specific (TASK-332)
  const [questionRows, setQuestionRows] = useState<QuestionRow[]>([]);

  // Validation errors
  const [nameError, setNameError] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [questionError, setQuestionError] = useState<string | null>(null);
  // Server-side validation error under the `questionnaire_fields` key.
  const [questionServerError, setQuestionServerError] = useState<string | null>(null);

  // ── Initialize form for edit mode ──────────────────────────────────────────
  const initFromService = useCallback((svc: Service) => {
    setStep('form');
    setSelectedType(svc.calculation_method as CalculationMethod);
    setName(svc.name);
    setDescription(svc.description || '');
    setKeywords((svc.keywords || []).join(', '));
    setSelectedParentId(svc.parent);

    if (svc.calculation_method === 'calculator') {
      const chain = svc.chains?.[0];
      if (chain?.required_inputs) {
        setRequiredInputs(
          chain.required_inputs
            .filter((inp): inp is { name: string; unit: string; type: string } =>
              typeof inp === 'object' && inp !== null && 'name' in inp
            )
            .map((inp) => ({
              name: inp.name || '',
              unit: inp.unit || '',
              type: inp.type || '',
            }))
        );
      }
      if (chain?.constants) {
        setConstants(
          Object.entries(chain.constants).map(([k, v]) => ({
            name: k,
            value: String(v),
          }))
        );
      }
      if (chain?.nodes_data) {
        const firstNode = Object.values(chain.nodes_data)[0];
        if (firstNode?.formula) {
          setFormula(String(firstNode.formula));
        }
      }
    } else if (
      svc.calculation_method === 'pricelist' ||
      svc.calculation_method === 'classifier'
    ) {
      if (Array.isArray(svc.pricing_data)) {
        setPricingRows(
          svc.pricing_data.map((item: PricingItem) => ({
            name: item.name || item.work || '',
            variant: item.variant || '',
            price: item.price !== undefined && item.price !== null ? String(item.price) : '',
            unit: item.unit || '',
          }))
        );
      }
      const chain = svc.chains?.[0];
      if (chain?.nodes_data) {
        const firstNode = Object.values(chain.nodes_data)[0];
        if (firstNode?.formula) {
          setPostFormulaEnabled(true);
          setPostFormula(String(firstNode.formula));
        }
      }
    } else if (svc.calculation_method === 'questionnaire') {
      // TASK-332: prefill question rows from questionnaire_fields.
      setQuestionRows(
        (svc.questionnaire_fields ?? []).map((f: QuestionnaireField) => ({
          name: f.name || '',
          type: f.type || 'text',
          unit: f.unit || '',
          description: f.description || '',
          required: f.required ?? false,
          options: (f.options ?? []).join(', '),
        }))
      );
    }
  }, []);

  // Reset or init when dialog opens/closes or service changes
  useEffect(() => {
    if (!open) return;
    if (isEdit && service) {
      initFromService(service);
    } else {
      setStep('type');
      setSelectedType(null);
      setName('');
      setDescription('');
      setKeywords('');
      setSelectedParentId(parentId ?? null);
      setRequiredInputs([]);
      setConstants([]);
      setFormula('');
      setPricingRows([]);
      setPostFormulaEnabled(false);
      setPostFormula('');
      setQuestionRows([]);
      setNameError(false);
      setPricingError(null);
      setQuestionError(null);
      setQuestionServerError(null);
    }
  }, [open, service, isEdit, initFromService, parentId]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  // KAN-157: the create→chain and update→chain→move orchestration used to
  // live here (mutationFn); it has been relocated into
  // `servicesApi.createWithChain`/`servicesApi.updateWithChain` (see
  // frontend/src/services/api/services.ts). These mutations are now thin
  // wrappers that just call those one-shot API methods.
  const createMutation = useMutation({
    mutationFn: (payload: CreateServiceData) => servicesApi.createWithChain(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast.success('Услуга создана');
      onClose();
    },
    onError: (error: unknown) => {
      // TASK-332: surface DRF validation errors for questionnaire_fields
      // next to the form instead of a generic toast.
      const fieldsError = extractFieldError(error, 'questionnaire_fields');
      if (fieldsError) {
        setQuestionServerError(fieldsError);
        return;
      }
      toast.error('Ошибка при создании услуги');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; data: Partial<CreateServiceData>; originalParent?: number | null }) =>
      servicesApi.updateWithChain(payload.id, payload.data, payload.originalParent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast.success('Услуга обновлена');
      onClose();
    },
    onError: (error: unknown) => {
      const fieldsError = extractFieldError(error, 'questionnaire_fields');
      if (fieldsError) {
        setQuestionServerError(fieldsError);
        return;
      }
      toast.error('Ошибка при обновлении услуги');
    },
  });

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleTypeSelect = (type: CalculationMethod) => {
    setSelectedType(type);
    setStep('form');
    setRequiredInputs([]);
    setConstants([]);
    setFormula('');
    setPricingRows([]);
    setPostFormulaEnabled(false);
    setPostFormula('');
    setQuestionRows([]);
    setNameError(false);
    setPricingError(null);
    setQuestionError(null);
    setQuestionServerError(null);
  };

  const handleSubmit = () => {
    // Validation
    setNameError(false);
    setPricingError(null);
    setQuestionError(null);
    setQuestionServerError(null);

    if (!name.trim()) {
      setNameError(true);
      return;
    }

    if (selectedType === 'pricelist' && pricingRows.length === 0) {
      setPricingError('Добавьте хотя бы одну позицию');
      return;
    }

    if (selectedType === 'classifier' && pricingRows.length === 0) {
      setPricingError('Добавьте хотя бы один тариф');
      return;
    }

    // TASK-332: a questionnaire needs at least one question, every question
    // needs a non-empty name, and enum questions need at least one option.
    if (selectedType === 'questionnaire') {
      if (questionRows.length === 0) {
        setQuestionError('Добавьте хотя бы один вопрос');
        return;
      }
      if (questionRows.some((r) => !r.name.trim())) {
        setQuestionError('Укажите имя каждого вопроса');
        return;
      }
      const enumWithoutOptions = questionRows.some(
        (r) =>
          r.type === 'enum' &&
          r.options.split(',').map((o) => o.trim()).filter(Boolean).length === 0
      );
      if (enumWithoutOptions) {
        setQuestionError('Укажите варианты выбора');
        return;
      }
    }

    // Build payload
    const keywordsArray = keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    const payload: CreateServiceData = {
      name: name.trim(),
      description: description.trim() || undefined,
      keywords: keywordsArray.length > 0 ? keywordsArray : undefined,
      calculation_method: selectedType || undefined,
    };

    payload.parent = selectedParentId;

    if (selectedType === 'calculator') {
      if (requiredInputs.length > 0) {
        payload.required_inputs = requiredInputs.map((r) => ({
          name: r.name,
          unit: r.unit,
          type: r.type,
        }));
      }
      if (constants.length > 0) {
        const constantsObj: Record<string, number> = {};
        constants.forEach((c) => {
          const val = parseFloat(c.value);
          if (!isNaN(val)) {
            constantsObj[c.name] = val;
          }
        });
        if (Object.keys(constantsObj).length > 0) {
          payload.constants = constantsObj;
        }
      }
      if (formula.trim()) {
        payload.formula = formula.trim();
      }
    } else if (selectedType === 'pricelist') {
      payload.pricing_data = pricingRows.map((r) => ({
        name: r.name,
        price: parseFloat(r.price) || 0,
        unit: r.unit,
      }));
      if (postFormulaEnabled && postFormula.trim()) {
        payload.post_formula = postFormula.trim();
      }
    } else if (selectedType === 'classifier') {
      payload.pricing_data = pricingRows.map((r) => ({
        work: r.name,
        variant: r.variant,
        price: parseFloat(r.price) || 0,
        unit: r.unit,
      }));
      if (postFormulaEnabled && postFormula.trim()) {
        payload.post_formula = postFormula.trim();
      }
    } else if (selectedType === 'questionnaire') {
      // TASK-332: map editor rows → API schema. unit only for number,
      // options only for enum, description only when filled.
      payload.questionnaire_fields = questionRows.map((r) => {
        const field: QuestionnaireField = {
          name: r.name.trim(),
          type: r.type,
          required: r.required,
        };
        if (r.type === 'number' && r.unit.trim()) {
          field.unit = r.unit.trim();
        }
        if (r.description.trim()) {
          field.description = r.description.trim();
        }
        if (r.type === 'enum') {
          field.options = r.options
            .split(',')
            .map((o) => o.trim())
            .filter(Boolean);
        }
        return field;
      });
    }

    if (isEdit && service) {
      updateMutation.mutate({ id: service.id, data: payload, originalParent: service.parent });
    } else {
      createMutation.mutate(payload);
    }
  };

  // ── Dynamic table handlers ─────────────────────────────────────────────────
  const addRequiredInputRow = () => {
    setRequiredInputs((prev) => [...prev, { name: '', unit: '', type: '' }]);
  };

  const removeRequiredInputRow = (index: number) => {
    setRequiredInputs((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRequiredInput = (
    index: number,
    field: keyof RequiredInputRow,
    value: string
  ) => {
    setRequiredInputs((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addConstantRow = () => {
    setConstants((prev) => [...prev, { name: '', value: '' }]);
  };

  const removeConstantRow = (index: number) => {
    setConstants((prev) => prev.filter((_, i) => i !== index));
  };

  const updateConstant = (
    index: number,
    field: keyof ConstantRow,
    value: string
  ) => {
    setConstants((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addPricingRow = () => {
    setPricingRows((prev) => [...prev, { name: '', variant: '', price: '', unit: '' }]);
  };

  const removePricingRow = (index: number) => {
    setPricingRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePricingRow = (
    index: number,
    field: keyof PricingRow,
    value: string
  ) => {
    setPricingRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  // Toggling the post-formula off also clears its text (pricelist/classifier).
  const handlePostFormulaEnabledChange = (enabled: boolean) => {
    setPostFormulaEnabled(enabled);
    if (!enabled) setPostFormula('');
  };

  // ── Questionnaire rows (TASK-332) ─────────────────────────────────────────
  const addQuestionRow = () => {
    setQuestionRows((prev) => [
      ...prev,
      { name: '', type: 'number', unit: '', description: '', required: false, options: '' },
    ]);
  };

  const removeQuestionRow = (index: number) => {
    setQuestionRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQuestionRow = (
    index: number,
    patch: Partial<QuestionRow>
  ) => {
    setQuestionRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // ── Parent service options (for the dropdown) ──────────────────────────────
  const { data: allServices = [] } = useQuery({
    queryKey: ['services'],
    queryFn: servicesApi.list,
    enabled: open,
    staleTime: 30_000,
  });

  const parentOptions = useMemo(() => {
    const excluded = isEdit && service ? getDescendantIds(service.id, allServices) : new Set<number>();
    const childrenMap = new Map<number | null, Service[]>();
    allServices.forEach(s => {
      const k = s.parent;
      if (!childrenMap.has(k)) childrenMap.set(k, []);
      childrenMap.get(k)!.push(s);
    });
    childrenMap.forEach(list => list.sort((a, b) => a.order - b.order));
    const result: { id: number; label: string }[] = [];
    const walk = (parentId: number | null, depth: number) => {
      (childrenMap.get(parentId) ?? []).forEach(s => {
        if (!excluded.has(s.id)) {
          if (s.calculation_method === 'calculator') {
            const indent = depth > 0 ? ' '.repeat(depth * 3) + '└ ' : '';
            result.push({ id: s.id, label: indent + s.name });
          }
          walk(s.id, depth + 1);
        }
      });
    };
    walk(null, 0);
    return result;
  }, [allServices, isEdit, service]);

  if (!open) return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        data-testid="create-service-dialog"
        className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Close button */}
        <button
          data-testid="dialog-close"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-0 z-10"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Закрыть</span>
        </button>

        {/* ── Step 1: Type selection ──────────────────────────────────────── */}
        {step === 'type' && !isEdit && (
          <>
            <DialogHeader>
              <DialogTitle>Новая услуга</DialogTitle>
              <DialogDescription>Выберите тип услуги</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3 py-4">
              <button
                data-testid="type-card-calculator"
                onClick={() => handleTypeSelect('calculator')}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
              >
                <Calculator className="h-8 w-8 text-blue-600" />
                <span className="font-medium text-sm">Калькулятор</span>
                <span className="text-xs text-gray-500 text-center">
                  Формулы и расчёты
                </span>
              </button>

              <button
                data-testid="type-card-pricelist"
                onClick={() => handleTypeSelect('pricelist')}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors cursor-pointer"
              >
                <List className="h-8 w-8 text-green-600" />
                <span className="font-medium text-sm">Прайс-лист</span>
                <span className="text-xs text-gray-500 text-center">
                  Позиции с ценами
                </span>
              </button>

              <button
                data-testid="type-card-classifier"
                onClick={() => handleTypeSelect('classifier')}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-colors cursor-pointer"
              >
                <Tag className="h-8 w-8 text-purple-600" />
                <span className="font-medium text-sm">Классификатор</span>
                <span className="text-xs text-gray-500 text-center">
                  Подбор по условиям
                </span>
              </button>

              <button
                data-testid="type-card-questionnaire"
                onClick={() => handleTypeSelect('questionnaire')}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-violet-400 hover:bg-violet-50 transition-colors cursor-pointer"
              >
                <ClipboardList className="h-8 w-8 text-violet-600" />
                <span className="font-medium text-sm">Опросник</span>
                <span className="text-xs text-gray-500 text-center">
                  Сбор параметров без цены
                </span>
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Form ────────────────────────────────────────────────── */}
        {step === 'form' && selectedType && (
          <>
            <DialogHeader>
              <DialogTitle>
                {isEdit ? 'Редактирование услуги' : 'Новая услуга'}
              </DialogTitle>
              <DialogDescription>
                {isEdit
                  ? 'Измените параметры услуги'
                  : `Заполните данные для ${METHOD_LABELS[selectedType].toLowerCase()}а`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Type badge (edit mode) */}
              {isEdit && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Тип:</span>
                  <Badge
                    data-testid="calculation-method-display"
                    variant="secondary"
                  >
                    {METHOD_LABELS[selectedType]}
                  </Badge>
                </div>
              )}

              {/* Name */}
              <div className="space-y-1.5">
                <Label htmlFor="service-name">Название *</Label>
                <Input
                  id="service-name"
                  data-testid="field-name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) setNameError(false);
                  }}
                  placeholder="Название услуги"
                />
                {nameError && (
                  <p data-testid="error-name" className="text-sm text-red-500">
                    Введите название услуги
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="service-description">Описание</Label>
                <Textarea
                  id="service-description"
                  data-testid="field-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Описание услуги"
                  rows={3}
                />
              </div>

              {/* Keywords */}
              <div className="space-y-1.5">
                <Label htmlFor="service-keywords">Ключевые слова</Label>
                <Input
                  id="service-keywords"
                  data-testid="field-keywords"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="через запятую"
                />
              </div>

              {/* Parent service */}
              <div className="space-y-1.5">
                <Label>Родительская услуга</Label>
                <Select
                  value={selectedParentId !== null ? String(selectedParentId) : '__none__'}
                  onValueChange={(val) =>
                    setSelectedParentId(val === '__none__' ? null : Number(val))
                  }
                >
                  <SelectTrigger data-testid="field-parent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!isEdit && (
                      <SelectItem value="__none__">— Корневая (без родителя)</SelectItem>
                    )}
                    {isEdit && selectedParentId === null && (
                      <SelectItem value="__none__" disabled>
                        — Корневая (нет родителя)
                      </SelectItem>
                    )}
                    {parentOptions.map(opt => (
                      <SelectItem key={opt.id} value={String(opt.id)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* ── Type-specific fields (KAN-157: extracted to create-form/) ── */}
              {selectedType === 'calculator' && (
                <CalculatorForm
                  requiredInputs={requiredInputs}
                  constants={constants}
                  formula={formula}
                  onFormulaChange={setFormula}
                  onAddRequiredInputRow={addRequiredInputRow}
                  onRemoveRequiredInputRow={removeRequiredInputRow}
                  onUpdateRequiredInput={updateRequiredInput}
                  onAddConstantRow={addConstantRow}
                  onRemoveConstantRow={removeConstantRow}
                  onUpdateConstant={updateConstant}
                />
              )}

              {selectedType === 'pricelist' && (
                <PricelistForm
                  pricingRows={pricingRows}
                  onAddPricingRow={addPricingRow}
                  onRemovePricingRow={removePricingRow}
                  onUpdatePricingRow={updatePricingRow}
                  pricingError={pricingError}
                  postFormulaEnabled={postFormulaEnabled}
                  onPostFormulaEnabledChange={handlePostFormulaEnabledChange}
                  postFormula={postFormula}
                  onPostFormulaChange={setPostFormula}
                />
              )}

              {selectedType === 'classifier' && (
                <ClassifierForm
                  pricingRows={pricingRows}
                  onAddPricingRow={addPricingRow}
                  onRemovePricingRow={removePricingRow}
                  onUpdatePricingRow={updatePricingRow}
                  pricingError={pricingError}
                  postFormulaEnabled={postFormulaEnabled}
                  onPostFormulaEnabledChange={handlePostFormulaEnabledChange}
                  postFormula={postFormula}
                  onPostFormulaChange={setPostFormula}
                />
              )}

              {selectedType === 'questionnaire' && (
                <QuestionnaireForm
                  questionRows={questionRows}
                  onAddQuestionRow={addQuestionRow}
                  onRemoveQuestionRow={removeQuestionRow}
                  onUpdateQuestionRow={updateQuestionRow}
                  questionError={questionError}
                  questionServerError={questionServerError}
                />
              )}
            </div>

            <DialogFooter>
              <Button
                data-testid="cancel-button"
                variant="outline"
                onClick={onClose}
                disabled={isPending}
              >
                Отмена
              </Button>
              <Button
                data-testid="submit-button"
                onClick={handleSubmit}
                disabled={isPending}
              >
                {isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {isEdit ? 'Сохранить' : 'Создать'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
